import { Keyboard, ListChecks, Play, X } from "lucide-react";
import { Fragment, useState } from "react";
import { useApp } from "../state/store.ts";
import { LegalFooter } from "./Legal.tsx";
import { replayTour, useOnboarding } from "../lib/onboarding.ts";
import { AcademyLink } from "./Academy.tsx";
import { AppVersion } from "./UpdateDialog.tsx";
import { openFeedback } from "../lib/feedback.ts";

/** One shortcut: alternatives separated by "/", combos by "+" (use "Plus" for the + key). */
type Row = [string, string];

const GROUPS: { title: string; rows: Row[] }[] = [
  {
    title: "Move around",
    rows: [
      ["W A S D / arrow keys", "Move"],
      ["Shift", "…faster (hold while moving)"],
      ["Q / E", "Rotate the view"],
      ["Plus / − / Wheel", "Zoom"],
      ["Left-drag", "Pan · right-drag rotates"],
      ["H", "Fly home"],
      ["Shift+H", "Set home at the mouse"],
      ["L", "Day & night lighting on/off"],
    ],
  },
  {
    title: "Tools",
    rows: [
      ["1", "Select / move"],
      ["2", "Path"],
      ["3", "Area"],
      ["4", "Building"],
      ["5", "Marker"],
      ["6", "Layouts & templates"],
      ["7", "Schematics: import & place builds"],
      ["Esc", "Back to select"],
    ],
  },
  {
    title: "Drawing",
    rows: [
      ["Click", "Add a point / corner"],
      ["Enter / Double-click", "Finish"],
      ["Click 1st point", "Close an area or building"],
      ["Shift+Click", "Building: 2nd corner = rectangle"],
      ["Backspace", "Remove the last point"],
      ["Esc", "Cancel"],
    ],
  },
  {
    title: "Editing",
    rows: [
      ["Click / Shift+Click", "Select · add to selection"],
      ["Drag", "Move the selection"],
      ["Drag a handle", "Reshape (Shift = free corner)"],
      ["Alt+Arrows", "Nudge 1 block (Shift = 10)"],
      ["R", "Rotate 90°"],
      ["F2 / Double-click tag", "Rename"],
      ["Ctrl+C / Ctrl+X / Ctrl+V", "Copy · cut · paste at mouse"],
      ["Ctrl+D", "Duplicate"],
      ["Ctrl+A", "Select all"],
      ["R / F / [ ]", "Placing a template: rotate · mirror · smaller/bigger"],
      ["R / F", "Schematic (placing or selected): turn · mirror"],
      ["Del", "Delete"],
      ["Ctrl+Z / Ctrl+Shift+Z", "Undo · redo"],
    ],
  },
  {
    title: "Save & share",
    rows: [
      ["Ctrl+S", "Save plan file (.mcplan)"],
      ["File", "Load a file · save / restore versions"],
      ["Plan together", "Go live, share the world, copy an invite link"],
      ["Ctrl+B", "Plan list: hide · show"],
      ["Ctrl+F", "Search the plan"],
      ["? / F1", "This overview"],
    ],
  },
];

/** mouse actions / UI names are shown as plain text, everything else as key caps */
const ACTIONS = new Set(["Click", "Double-click", "Left-drag", "Drag", "Wheel", "Click 1st point", "Drag a handle", "Double-click tag", "File", "Plan together"]);

function Keys({ spec }: { spec: string }) {
  const alts = spec.split(" / ");
  return (
    <span className="keys">
      {alts.map((alt, i) => (
        <Fragment key={i}>
          {i > 0 && <span className="or">or</span>}
          {alt.split("+").map((k, j) => (
            <Fragment key={j}>
              {j > 0 && <span className="plus">+</span>}
              {ACTIONS.has(k)
                ? <span className="act">{k}</span>
                : <kbd>{k === "Plus" ? "+" : k}</kbd>}
            </Fragment>
          ))}
        </Fragment>
      ))}
    </span>
  );
}

/** Full-screen shortcut overview (? / F1 / the ? button). */
export function HelpOverlay() {
  const open = useApp((s) => s.helpOpen);
  const set = useApp((s) => s.set);
  if (!open) return null;
  return (
    <div className="modal-back" onClick={() => set({ helpOpen: false })}>
      <div className="modal help-overlay" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Keyboard shortcuts">
        <div className="modal-head">
          <span className="modal-icon"><Keyboard size={18} aria-hidden="true" /></span>
          <div className="grow">
            <h2>Shortcuts</h2>
            <p className="caption">On a Mac use <kbd>⌘</kbd> instead of <kbd>Ctrl</kbd>. Keys work by position, so QWERTZ and AZERTY keyboards move the same way.</p>
          </div>
          <button className="btn btn-ghost btn-icon" onClick={() => set({ helpOpen: false })} title="Close · Esc" aria-label="Close"><X size={16} aria-hidden="true" /></button>
        </div>
        <div className="help-grid">
          {GROUPS.map((g) => (
            <section key={g.title}>
              <h4 className="overline">{g.title}</h4>
              <table>
                <tbody>
                  {g.rows.map(([k, d]) => (
                    <tr key={k + d}><td><Keys spec={k} /></td><td>{d}</td></tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
        <div className="help-onboarding">
          <button className="btn btn-outline" onClick={() => { set({ helpOpen: false, tool: "select" }); replayTour(); }}><Play size={14} aria-hidden="true" />Replay the tour</button>
          <button className="btn btn-outline" onClick={() => { set({ helpOpen: false }); useOnboarding.getState().set({ checklistHidden: false, tourDone: true }); }}><ListChecks size={14} aria-hidden="true" />Show getting started</button>
          <span className="help-links">
            <AcademyLink to="home">Academy</AcademyLink>
            <AcademyLink to="troubleshooting">Troubleshooting</AcademyLink>
            {/* a post on MC Planner's feedback board, written here (ui/Feedback.tsx); the dialog links to the board */}
            <button className="link help-feedback" onClick={() => { set({ helpOpen: false }); openFeedback(); }}>Send feedback</button>
          </span>
        </div>
        <div className="help-foot">
          <AppVersion />
          <LegalFooter />
        </div>
      </div>
    </div>
  );
}

const TOOL_NAME = { path: "Path", area: "Area", building: "Building", marker: "Marker" } as const;

/** Large hint at the bottom of the map while a tool is active or something is being moved. */
export function ToolHint() {
  const tool = useApp((s) => s.tool);
  const info = useApp((s) => s.draftInfo);
  if (tool === "select" && !info) return null;
  return (
    <div className="tool-hint glass" role="status">
      {tool !== "select" && <b>{TOOL_NAME[tool]}</b>}
      {tool !== "select" && info && <span className="dot-sep" aria-hidden="true" />}
      <span>{info}</span>
      {tool !== "select" && <span className="esc"><kbd>Esc</kbd> cancel</span>}
    </div>
  );
}
